export function isDefined<T = unknown>(
  value: T | undefined | null
): value is Exclude<T, undefined | null> {
  return value !== undefined && value !== null
}

interface RGB extends Record<any, any> {
  r: number
  g: number
  b: number
  a?: number
}

export interface RGBColor extends RGB {
  a?: 1
  format?: 'name' | 'rgb'
}

export interface RGBAColor extends RGB {
  a: number
  format?: 'name' | 'rgba'
}

export interface HEX3Color extends RGB {
  a?: 1
  format?: 'name' | 'hex3'
}
export interface HEX4Color extends RGB {
  a: number
  format?: 'name' | 'hex4'
}
export interface HEX6Color extends RGB {
  a?: 1
  format?: 'name' | 'hex6'
}
export interface HEX8Color extends RGB {
  a: number
  format?: 'name' | 'hex8'
}

interface HSL extends Record<any, any> {
  h: number
  s: number
  l: number
  a?: number
}

export interface HSLColor extends HSL {
  a?: 1
  format?: 'name' | 'hsl'
}

export interface HSLAColor extends HSL {
  a: number
  format?: 'name' | 'hsla'
}

interface HSV extends Record<any, any> {
  h: number
  s: number
  v: number
  a?: number
}

export interface HSVColor extends HSV {
  a?: 1
  format?: 'name' | 'hsv'
}

export interface HSVAColor extends HSV {
  a: number
  format?: 'name' | 'hsva'
}

export type Color =
  | string
  | RGBColor
  | RGBAColor
  | HSLColor
  | HSLAColor
  | HSVColor
  | HSVAColor
  | HEX3Color
  | HEX4Color
  | HEX6Color
  | HEX8Color

export type ObjectColor = Exclude<Color, string>

export type ColorType = 'hex' | 'rgb' | 'hsv' | 'hsl'

/**
 * 将原始的 h、s、l 值标准化为 0 ~ 1 的值
 *
 * @param h 0 ~ 360
 * @param s 0 ~ 1，0% ~ 100%
 * @param l 0 ~ 1，0% ~ 100%
 *
 * @returns 标准化后的 HSL 对象
 */
export function normalizeHsl(
  h: number | string,
  s: number | string,
  l: number | string
) {
  return {
    h: boundRange(h, 0, 360) / 360,
    s: boundRange(isPercentage(s) ? parsePercentage(s) : s, 0, 1),
    l: boundRange(isPercentage(l) ? parsePercentage(l) : l, 0, 1),
  }
}

/**
 * 将原始的 r、g、b 值标准化为 0 ~ 1 的值
 *
 * @param r 0 ~ 255
 * @param g 0 ~ 255
 * @param b 0 ~ 255
 *
 * @returns 标准化后 RGB 对象
 */
export function normalizeRgb(
  r: number | string,
  g: number | string,
  b: number | string
) {
  return {
    r: boundRange(r, 0, 255) / 255,
    g: boundRange(g, 0, 255) / 255,
    b: boundRange(b, 0, 255) / 255,
  }
}

/**
 * 将原始透明度值标准化为 0 ~ 1 的值
 *
 * @param a 0 ~ 1，0% ~ 100%
 *
 * @returns 标准化后的透明度
 */
export function normalizeAlpha(a: number | string) {
  return boundRange(isPercentage(a) ? parsePercentage(a) : a, 0, 1)
}

/**
 * 将 HSL 颜色转换为 RGB 颜色
 *
 * @param h 0 ~ 360
 * @param s 0 ~ 1，0% ~ 100%
 * @param l 0 ~ 1，0% ~ 100%
 *
 * @returns 转换后的 RGB 颜色
 */
export function hslToRgb(
  h: number | string,
  s: number | string,
  l: number | string
): RGBColor {
  let r, g, b
  ;({ h, s, l } = normalizeHsl(h, s, l))

  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? 1 * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hueToRgb(p, q, h + 1 / 3)
    g = hueToRgb(p, q, h)
    b = hueToRgb(p, q, h - 1 / 3)
  }

  r *= 255
  g *= 255
  b *= 255

  return { r, g, b, toString: toRgbString }
}

/**
 * 将 HSL 颜色转换为 HSV 颜色
 *
 * @param h 0 ~ 360
 * @param s 0 ~ 1，0% ~ 100%
 * @param l 0 ~ 1，0% ~ 100%
 *
 * @returns 转换后的 HSV 颜色
 */
export function hslToHsv(
  h: number | string,
  s: number | string,
  l: number | string
): HSVColor {
  ;({ h, s, l } = normalizeHsl(h, s, l))

  const v = 0.5 * (2 * l + s * (1 - Math.abs(2 * l - 1)))

  s = (2 * (v - l)) / v

  return { h: h * 360, s, v, toString: toHsvString }
}

/**
 * 将 RGB 颜色转换为 HEX 颜色
 *
 * @param r 0 ~ 255
 * @param g 0 ~ 255
 * @param b 0 ~ 255
 * @param allow3Char 是否允许 3 位的 HEX 值
 *
 * @returns 转换后的 HEX 颜色
 */
export function rgbToHex(
  r: number | string,
  g: number | string,
  b: number | string,
  allow3Char = false
) {
  ;({ r, g, b } = normalizeRgb(r, g, b))

  const hex = [
    repairDigits(Math.round(r * 255).toString(16)),
    repairDigits(Math.round(g * 255).toString(16)),
    repairDigits(Math.round(b * 255).toString(16)),
  ]

  if (
    allow3Char &&
    isRepetitive(hex[0]) &&
    isRepetitive(hex[1]) &&
    isRepetitive(hex[2])
  ) {
    return hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0)
  }

  return `#${hex.join('')}`
}

/**
 * 将 RGBA 颜色转换为 HEX 颜色
 *
 * @param r 0 ~ 255
 * @param g 0 ~ 255
 * @param b 0 ~ 255
 * @param a 0 ~ 1，0% ~ 100%
 * @param allow4Char 是否允许 4 位的 HEX 值
 *
 * @returns 转换后的 HEX 颜色
 */
export function rgbaToHex(
  r: number | string,
  g: number | string,
  b: number | string,
  a: number | string,
  allow4Char = false
) {
  ;({ r, g, b } = normalizeRgb(r, g, b))

  const hex = [
    repairDigits(Math.round(r * 255).toString(16)),
    repairDigits(Math.round(g * 255).toString(16)),
    repairDigits(Math.round(b * 255).toString(16)),
    repairDigits(convertDecimalToHex(normalizeAlpha(a))),
  ]

  if (
    allow4Char &&
    isRepetitive(hex[0]) &&
    isRepetitive(hex[1]) &&
    isRepetitive(hex[2]) &&
    isRepetitive(hex[3])
  ) {
    return (
      hex[0].charAt(0) + hex[1].charAt(0) + hex[2].charAt(0) + hex[3].charAt(0)
    )
  }

  return `#${hex.join('')}`
}

/**
 * 随机生成一个特定色调（冷暖色）的颜色值
 *
 * @param prefer 色调 hard-冷色调，soft-暖色调
 * @param withAlpha 是否具有透明度
 * @param type 颜色的类型
 *
 * @returns 生成的颜色字面值
 */
export function randomPreferColor(
  prefer: 'hard' | 'soft',
  withAlpha = false,
  type: ColorType = 'hex'
) {
  const h = Math.round(Math.random() * 360)
  const s =
    Math.round(
      prefer === 'hard' ? 80 + Math.random() * 20 : 20 + Math.random() * 70
    ) / 100
  const l =
    Math.round(
      prefer === 'hard' ? 40 + Math.random() * 20 : 80 + Math.random() * 15
    ) / 100

  if (type === 'hsl') {
    return toHslString.bind({ h, s, l })()
  }

  let color!: ObjectColor

  if (type === 'hex' || type === 'rgb') {
    color = hslToRgb(h, s, l)

    if (type === 'hex') {
      const { r, g, b } = color

      return withAlpha ? rgbaToHex(r, g, b, Math.random()) : rgbToHex(r, g, b)
    }
  } else if (type === 'hsv') {
    color = hslToHsv(h, s, l)
  }

  if (withAlpha) {
    ;(color as RGBAColor).a = Math.random()
  }

  return color.toString()
}

/**
 * 随机生成一个冷色调的颜色值
 *
 * @param withAlpha 是否具有透明度
 * @param type 颜色的类型
 *
 * @returns 生成的颜色字面值
 */
export function randomHardColor(withAlpha = false, type: ColorType = 'hex') {
  return randomPreferColor('hard', withAlpha, type)
}

function repairDigits(str: string) {
  return str.length === 1 ? `0${str}` : str.toString()
}

function isRepetitive(string: string): boolean {
  return string.charAt(0) === string.charAt(1)
}

function convertDecimalToHex(number: number | string): string {
  return Math.round(Number.parseFloat(number as string) * 255).toString(16)
}

function boundRange(number: number | string, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.parseFloat(number as string)))
}

function hueToRgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1
  if (t > 1) t -= 1
  if (t < 1 / 6) return p + (q - p) * 6 * t
  if (t < 1 / 2) return q
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6

  return p
}

function isPercentage(percent: number | string): boolean {
  return String(percent).trim().includes('%')
}

function parsePercentage(percent: number | string): number {
  const number = Number.parseFloat(percent as string) / 100

  return Number.isNaN(number) ? 0 : number
}

function toRgbString(this: RGB) {
  if (isDefined(this.a) && this.a >= 0 && this.a < 1) {
    return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`
  }

  return `rgb(${this.r}, ${this.g}, ${this.b})`
}

function toHslString(this: HSL) {
  const s = `${this.s * 100}%`
  const l = `${this.l * 100}%`

  if (isDefined(this.a) && this.a >= 0 && this.a < 1) {
    return `hsla(${this.h}, ${s}, ${l}, ${this.a})`
  }

  return `hsl(${this.h}, ${s}, ${l})`
}

function toHsvString(this: HSV) {
  const s = `${this.s * 100}%`
  const v = `${this.v * 100}%`

  if (isDefined(this.a) && this.a >= 0 && this.a < 1) {
    return `hsva(${this.h}, ${s}, ${v}, ${this.a})`
  }

  return `hsv(${this.h}, ${s}, ${v})`
}
